import {
    Box, Button, Flex, Grid, HStack, Input, List, ListItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, useColorModeValue, VStack
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { MdEmail } from 'react-icons/md';
import { toast } from "react-toastify";
import { sendCommunications } from 'services/CommunicationService';
import { getAllPaginated } from 'services/ModelService';
import { getAll } from 'services/ModelTypeService';
import { validateEmail } from 'utils/common-utils';
import { Customer, GetModelPaginatedRs, GetModelTypeRs, Model } from 'utils/interfaces';

const NewCommunicationsModal = (
    { isOpen,
        onClose,
        customer
    }: {
        isOpen: boolean;
        onClose: () => void,
        customer: Customer
    }
) => {

    const { keycloak } = useKeycloak();

    const [modelTypes, setModelTypes] = useState<GetModelTypeRs[]>([]);
    const [models, setModels] = useState<GetModelPaginatedRs>();

    const [selectedModelType, setSelectedModelType] = useState<GetModelTypeRs | null>(null);
    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [additionalRecipients, setAdditionalRecipients] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState<string>(""); // Stato per l'email corrente

    const [subject, setSubject] = useState<string>("");
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // Stato per i file selezionati

    const [modelLoading, setModelLoading] = useState<boolean>(false);
    const [sendCommunicationLoading, setSendCommunicationLoading] = useState<boolean>(false);


    useEffect(() => {
        const fetchModelTypes = async () => {
            const response = await getAll(keycloak.token, onSessionExpired);
            setModelTypes(response);
        };

        if (isOpen) {
            fetchModelTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            setSelectedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]); // Aggiungi i nuovi file a quelli esistenti
        }
    };

    const onSessionExpired = () => {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
            position: "bottom-right",
            onClose: () => {
                keycloak.logout();
                localStorage.removeItem("kc-token");
            }
        });
    };

    const handleModelTypeClick = async (modelType: GetModelTypeRs) => {
        setSelectedModelType(modelType);
        await fetchModels({ modelTypeId: modelType.id });
    };

    const shadow = useColorModeValue(
        '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
        '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
    );

    const fetchModels = async (filters: any) => {
        setModelLoading(true);
        try {
            const modelsData = await getAllPaginated(
                keycloak.token,
                onSessionExpired,
                filters
            );

            setModels(modelsData);
        } catch (err) {
            console.error(err);
            toast.error(
                "Errore durante l'acquisizione dei modelli, perfavore riprova.", {
                position: "bottom-right",
            });
        } finally {
            setModelLoading(false);
        }
    };

    const resetState = () => {
        setSelectedModelType(null);
        setSelectedModel(null);
        setModels(undefined);
        setAdditionalRecipients([]);
        setSubject("");
        setSelectedFiles([]);
    };

    const sendCommunicationsOnBackend = async () => {
        setSendCommunicationLoading(true);
        try {
            const communicationsResponse = await sendCommunications(
                keycloak.token,
                onSessionExpired,
                additionalRecipients,
                subject,
                selectedModelType?.id,
                selectedModel?.id,
                [customer.id],
                selectedFiles
            );

            if (!communicationsResponse) {
                return;
            }

            toast.success(
                "Comunicazione inviata con successo", {
                position: "bottom-right",
            });

            // Ritarda la chiusura della modale per permettere al toast di essere visualizzato
            setTimeout(() => {
                resetState();
                onClose();
            }, 50);

        } catch (err) {
            console.error(err);
            toast.error(
                "Errore durante l'invio della comunicazione, perfavore riprova.", {
                position: "bottom-right",
            });
        } finally {
            setSendCommunicationLoading(false);
        }
    }

    return (
        <Modal isOpen={isOpen}
            onClose={() => {
                resetState();
                onClose();
            }}
            size="6xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader backgroundColor="white" color="#9FB50C" zIndex={1} textAlign="left">
                    Nuova Comunicazione
                </ModalHeader>
                <ModalCloseButton _focus={{ boxShadow: 'none' }} zIndex={10} />
                <ModalBody>
                    <HStack
                        alignItems="flex-start"
                        spacing={4}
                        flexDirection={{ base: "column", md: "row" }} // Colonne verticali su dispositivi mobili, orizzontali su desktop
                        width="100%"
                    >                        {/* Colonna sinistra con i tipi di modelli */}
                        <VStack alignItems="flex-start" spacing={4} width={{ base: "100%", md: "30%" }}>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                                height="300px"
                                overflowY="auto"
                                css={{
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#688CC8',
                                        borderRadius: '24px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#688CC8',
                                    },
                                }}
                            >
                                <Text fontWeight="bold" mb={4}>Tipo Modello: <Text as="span" color="red">*</Text></Text>
                                <List spacing={3}>
                                    {modelTypes?.map((modelType) => (
                                        <ListItem
                                            key={modelType.id}
                                            cursor="pointer"
                                            onClick={() => handleModelTypeClick(modelType)}
                                            bg={selectedModelType?.id === modelType.id ? "gray.300" : "white"}
                                            p={2}
                                            borderRadius="md"
                                        >
                                            {modelType.type}
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                                height="300px"
                                overflowY="auto"
                                css={{
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#688CC8',
                                        borderRadius: '24px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#688CC8',
                                    },
                                }}
                            >
                                <Text fontWeight="bold" mb={2}>Modello: <Text as="span" color="red">*</Text></Text>
                                {modelLoading ? (
                                    <Flex justifyContent='center' alignItems='center' height='50%'>
                                        <Spinner size='xl' color='#9FB50C' />
                                    </Flex>
                                ) : (
                                    <List spacing={3}>
                                        {models?.content.map((model) => (
                                            <ListItem
                                                key={model.id}
                                                cursor="pointer"
                                                onClick={() => setSelectedModel(model)}
                                                bg={selectedModel?.id === model.id ? "gray.300" : "white"}
                                                p={2}
                                                borderRadius="md"
                                            >
                                                {model.name}
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </VStack>

                        {/* Sezioni a destra */}
                        <VStack alignItems="flex-start" spacing={4} width={{ md: "70%" }}>
                            <Box
                                backgroundColor="white"
                                borderRadius="20px"
                                boxShadow={shadow}
                                width="100%"
                                p={4}
                            >
                                <Grid mb={2}>
                                    <Text fontWeight="bold" mb={2}>Email Destinatario: <Text as="span" color="red">*</Text></Text>
                                    <Text mb={2}>
                                        {customer.email}
                                    </Text>
                                </Grid>
                                <Box>
                                    <Text fontWeight="bold" mb={2}>
                                        Destinatari Extra:
                                    </Text>
                                    <Box
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        p={2}
                                        minHeight="50px"
                                        display="flex"
                                        flexWrap="wrap"
                                        alignItems="center"
                                    >
                                        {additionalRecipients.map((email, index) => (
                                            <Flex
                                                key={index}
                                                alignItems="center"
                                                bg="gray.200"
                                                borderRadius="md"
                                                p={1}
                                                m={1}
                                            >
                                                <Text mr={2}>{email}</Text>
                                                <Button
                                                    size="xs"
                                                    colorScheme="red"
                                                    onClick={() => {
                                                        setAdditionalRecipients((prev) => prev.filter((_, i) => i !== index));
                                                    }}
                                                >
                                                    x
                                                </Button>
                                            </Flex>
                                        ))}
                                        <Input
                                            placeholder="Aggiungi email"
                                            value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && newEmail.trim() !== "") {
                                                    if (validateEmail(newEmail)) {
                                                        setAdditionalRecipients((prev) => [...prev, newEmail.trim()]);
                                                        setNewEmail("");
                                                    } else {
                                                        toast.error("Email non valida", {
                                                            position: "bottom-right",
                                                        });
                                                    }
                                                }
                                            }}
                                            variant="unstyled"
                                            flex="1"
                                        />
                                    </Box>
                                </Box>
                                <Box mt={4}>
                                    <Text fontWeight="bold" mb={2}>Oggetto: <Text as="span" color="red">*</Text></Text>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Inserisci l'oggetto"
                                    />
                                </Box>
                                <Box mt={4}>
                                    <Text fontWeight="bold" mb={2}>
                                        Allegati:
                                    </Text>
                                    <Box
                                        p={4}
                                        border="1.5px dashed gray" // Bordi tratteggiati
                                        borderRadius="md"
                                        textAlign="center"
                                        onDragOver={(e) => e.preventDefault()} // Previeni il comportamento predefinito del drag
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const files = e.dataTransfer.files;
                                            if (files) {
                                                setSelectedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]); // Aggiungi i file trascinati allo stato
                                            }
                                        }}
                                    >
                                        <Text color="gray.600" fontWeight={"bold"}>Trascina qui i file da allegare</Text>
                                        <Text color="gray.400">Dimensione massima consentita: 50MB</Text>
                                        <Input
                                            id="file-upload"
                                            type="file"
                                            multiple
                                            display="none" // Nascondi l'input
                                            onChange={handleFileChange} // Gestisci il cambiamento dei file
                                            isDisabled={sendCommunicationLoading}
                                        />
                                        <Button
                                            as="label"
                                            htmlFor="file-upload"
                                            mt={2}
                                            colorScheme="blue"
                                            backgroundColor="#688CC8"
                                            color="white"
                                            cursor="pointer"
                                            _hover={{ backgroundColor: "#5677A3" }}
                                            isDisabled={sendCommunicationLoading}
                                        >
                                            Oppure Carica
                                        </Button>
                                    </Box>

                                    {/* Lista dei file selezionati */}
                                    {selectedFiles.length > 0 && (
                                        <Box mt={4}>
                                            <Text textColor={"gray.400"} fontSize="sm" mb={2}>
                                                File selezionati:
                                            </Text>
                                            {selectedFiles.map((file, index) => (
                                                <Flex key={index} alignItems="center" mb={1}>
                                                    <Text as="span" mr={2}>
                                                        {file.name}
                                                    </Text>
                                                    <Button
                                                        size="xs"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            // Rimuovi il file selezionato dallo stato
                                                            setSelectedFiles((prevFiles) =>
                                                                prevFiles.filter((_, i) => i !== index)
                                                            );
                                                        }}
                                                    >
                                                        x
                                                    </Button>
                                                </Flex>
                                            ))}
                                        </Box>
                                    )}

                                </Box>
                            </Box>
                        </VStack>
                    </HStack>
                </ModalBody>
                <ModalFooter backgroundColor="white" zIndex={1}>
                    <Button
                        colorScheme={"#688CC8"}
                        backgroundColor={'#688CC8'}
                        color="white"
                        mr={3}
                        onClick={() => {
                            sendCommunicationsOnBackend();
                        }}
                        leftIcon={sendCommunicationLoading ? <Spinner size="sm" /> : <MdEmail />} // Mostra il loader se loading
                        isDisabled={!selectedModelType || !selectedModel || !subject || sendCommunicationLoading}
                    >
                        {sendCommunicationLoading ? "Invio..." : "Invia"} {/* Cambia il testo del pulsante */}
                    </Button>
                    <Button
                        textColor={"gray.900"}
                        colorScheme="gray"
                        onClick={() => {
                            resetState();
                            onClose();
                        }}
                    >
                        Annulla
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NewCommunicationsModal;